import type { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import Button from '~/components/common/Button';
import Logo from '~/components/common/Logo';
import Section from '~/components/hero/LandingPage/Section';
import { LeaderBoard } from '~/components/leaderboard/Leaderboard';
import MainNav from '~/components/navbar/MainNav';
import { FILTERS } from '~/constants';
import { ConnectedProviders } from '~/contextProviders/connectedProviders';
import { useConnectedWallet } from '~/contextProviders/ConnectedWallerProvider';
import { useGetRankings } from '~/hooks/game/useGetRankings';
import { useGetRankingsTotalStats } from '~/hooks/game/useGetRankingsTotalStats';
import { useGetOwnedNFTs } from '~/hooks/useGetOwnedNFTs';

const Leaderboard: NextPage<{ page: number }> = ({ page }) => {
  const [currentPage, setCurrentPage] = useState(page);
  const [characterFilters, setCharacterFilters] = useState([]);
  const [levelsFilters, setLevelsFilters] = useState([]);
  const [syndicateFilters, setSyndicateFilters] = useState([]);
  const [typeFilters, setTypeFilters] = useState([]);
  const [hpFilters, setHPFilters] = useState([]);
  const [ownedFilters, setOwnedFilters] = useState([]);
  const [contextFilters, setContextFilters] = useState(false);

  const filters = {
    [FILTERS.characters]: characterFilters,
    [FILTERS.levels]: levelsFilters,
    [FILTERS.syndicates]: syndicateFilters,
    [FILTERS.types]: typeFilters,
    [FILTERS.hps]: hpFilters,
    [FILTERS.owned]: ownedFilters,
    [FILTERS.context]: contextFilters,
  };
  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    fetchPreviousPage,
    isFetchingPreviousPage,
  } = useGetRankings({
    enabled: page !== undefined,
    filters,
    page: currentPage,
  });
  const { data: statTotals } = useGetRankingsTotalStats({
    enabled: page !== undefined,
  });
  const { account } = useConnectedWallet();
  const { data: ownedWarriorRankings } = useGetOwnedNFTs({
    address: account?.address,
  });

  const router = useRouter();
  useEffect(() => {
    if (page && currentPage && page !== currentPage) {
      router.replace(`/leaderboard/?page=${currentPage}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const resultPage = data?.pages.find((p) => p?.page === currentPage);
  let totalPage = 0;
  if (resultPage) {
    totalPage = Math.ceil(resultPage?.total / resultPage?.limit);
  }

  return (
    <>
      <div className="container container--landing-page container--landing-page-mint">
        <header className="landing-page__header">
          <MainNav showConnectWallet={true} />
        </header>
        <div
          className={`content-wrapper ${
            resultPage?.results && resultPage?.results.length ?
              '' :
              'content-wrapper--full'
          }`}
        >
          <Section className="landing-page__section--leaderboard">
            <Logo filename="leaderboard-logo.svg" alt="Leaderboard page" />
            {isLoading && <div className="loading-spinner">Loading...</div>}
            {!isLoading && (
              <LeaderBoard
                page={resultPage}
                ownedWarriorRankings={ownedWarriorRankings}
                filterStates={{
                  characters: characterFilters,
                  levels: levelsFilters,
                  syndicates: syndicateFilters,
                  types: typeFilters,
                  hps: hpFilters,
                  owned: ownedFilters,
                  context: contextFilters,
                }}
                setFilters={{
                  characters: setCharacterFilters,
                  levels: setLevelsFilters,
                  syndicates: setSyndicateFilters,
                  types: setTypeFilters,
                  hps: setHPFilters,
                  owned: setOwnedFilters,
                  context: setContextFilters,
                }}
                statTotals={statTotals}
              />
            )}
            {!!resultPage?.results.length && (
              <div className="leaderboard__pagination">
                {/* Previous */}
                <Button
                  className="leaderboard__btn__navigation"
                  label="Previous"
                  onClick={() => {
                    fetchPreviousPage().then(() =>
                      setCurrentPage(currentPage - 1)
                    );
                  }}
                  disabled={isFetchingPreviousPage || currentPage <= 1}
                />

                {/* first page */}
                {currentPage !== 1 && (
                  <Button
                    className="leaderboard__btn__navigation"
                    label="1"
                    onClick={() => {
                      fetchNextPage().then(() => setCurrentPage(1));
                    }}
                    disabled={isFetchingNextPage}
                  />
                )}

                {/* current page */}
                <Button
                  className="leaderboard__btn__navigation"
                  label={currentPage}
                  disabled={true}
                />

                {/* current page + 1 */}
                {resultPage?.hasMore && currentPage + 1 !== totalPage && (
                  <Button
                    className="leaderboard__btn__navigation"
                    label={currentPage + 1}
                    onClick={() => {
                      fetchNextPage().then(() =>
                        setCurrentPage(currentPage + 1)
                      );
                    }}
                  />
                )}

                {/* next page */}
                <Button
                  className="leaderboard__btn__navigation"
                  label="Next"
                  onClick={() => {
                    fetchNextPage().then(() => setCurrentPage(currentPage + 1));
                  }}
                  disabled={isFetchingNextPage || !resultPage?.hasMore}
                />

                {/* last page */}
                {totalPage !== currentPage && (
                  <Button
                    className="leaderboard__btn__navigation"
                    label={totalPage}
                    onClick={() => {
                      setCurrentPage(totalPage);
                    }}
                    disabled={isFetchingNextPage || currentPage === totalPage}
                  />
                )}
              </div>
            )}
          </Section>
        </div>
      </div>
    </>
  );
};

const LeaderboardWrapper = () => {
  // const router = useRouter();
  const router = useRouter();
  const [currentPage, setCurrentPage] = useState(0);
  useEffect(() => {
    if (!router.isReady) return;
    const page: number = parseInt((router?.query?.page as string) || '1');
    setCurrentPage(page);
    // codes using router.query
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);
  if (currentPage) {
    return <Leaderboard page={currentPage} />;
  } else {
    return <></>;
  }
};

const LeaderBoardPage = () => {
  return (
    <ConnectedProviders>
      <LeaderboardWrapper />
    </ConnectedProviders>
  );
};

export default LeaderBoardPage;
